"use client";

import { useMemo, useState, useTransition } from "react";
import { useRouter } from "@/lib/navigation";
import {
  Check,
  ChevronsUpDown,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  notesFeedFilterSearchParams,
  type NotesFeedBrowseSort,
} from "@/lib/notes-feed-filters";
import type { NotesFeedTagSuggestion } from "@/lib/notes-feed";
import type { NotesFeedSourceKey } from "@/lib/notes-feed-remote";
import { cn } from "@/lib/utils";

const TAG_KIND_LABELS: Record<NotesFeedTagSuggestion["kind"], string> = {
  company: "Company / ticker",
  theme: "Theme",
  model: "Model",
  note_type: "Note type",
  status: "Status",
  topic: "Topic",
};

export function NotesFeedFilters({
  initialSearch,
  initialSort,
  initialSourceKeys,
  initialTagKey,
  sources,
  tags,
}: {
  initialSearch: string;
  initialSort: NotesFeedBrowseSort;
  initialSourceKeys: NotesFeedSourceKey[];
  initialTagKey: string;
  sources: { key: NotesFeedSourceKey; name: string }[];
  tags: NotesFeedTagSuggestion[];
}) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [search, setSearch] = useState(initialSearch);
  const [sort, setSort] = useState<NotesFeedBrowseSort>(initialSort);
  const [selectedSources, setSelectedSources] = useState(
    () => new Set(initialSourceKeys),
  );
  const [tagKey, setTagKey] = useState(initialTagKey);
  const [tagOpen, setTagOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const selectedTag = useMemo(
    () => tags.find((tag) => tag.key === tagKey) ?? null,
    [tagKey, tags],
  );
  const allSourcesSelected = selectedSources.size === sources.length;
  const hasSearch = search.trim().length > 0;
  const activeFilterCount =
    (allSourcesSelected ? 0 : 1) +
    (tagKey ? 1 : 0) +
    (sort !== "newest" ? 1 : 0);

  function apply(nextSort = sort) {
    const sourceKeys = sources
      .map((source) => source.key)
      .filter((sourceKey) => selectedSources.has(sourceKey));
    if (sourceKeys.length === 0) return;
    const params = notesFeedFilterSearchParams({
      search: search.trim(),
      sort: !hasSearch && nextSort === "relevance" ? "newest" : nextSort,
      sourceKeys,
      tagKey,
    });
    startTransition(() => {
      setFilterOpen(false);
      router.push(params.size > 0 ? `/notes?${params.toString()}` : "/notes");
    });
  }

  return (
    <form
      className="flex min-w-0 flex-col gap-2"
      id="notes-feed-filter-form"
      onSubmit={(event) => {
        event.preventDefault();
        apply();
      }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            aria-label="Search the Notes feed"
            className="border-border/70 bg-background pr-3 pl-9 text-sm shadow-none transition-colors focus:border-primary atlas-v2:border-input atlas-v2:bg-background atlas-v2:dark:bg-background atlas-v2:text-base atlas-v2:sm:text-sm atlas-v2:focus:border-ring"
            maxLength={200}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Company, topic or date…"
            type="search"
            value={search}
          />
        </div>
        <Button disabled={pending} size="sm" type="submit">
          Search
        </Button>
        <Popover onOpenChange={setFilterOpen} open={filterOpen}>
          <PopoverTrigger asChild>
            <Button
              aria-expanded={filterOpen}
              aria-label={
                activeFilterCount > 0
                  ? `Filter and sort Notes feed, ${activeFilterCount} active`
                  : "Filter and sort Notes feed"
              }
              className={cn(
                "relative shrink-0",
                activeFilterCount > 0 &&
                  "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15",
              )}
              size="icon"
              type="button"
              variant="outline"
            >
              <SlidersHorizontal aria-hidden />
              {activeFilterCount > 0 ? (
                <span
                  aria-hidden
                  className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-primary text-[9px] font-semibold leading-none text-primary-foreground shadow-sm"
                >
                  {activeFilterCount}
                </span>
              ) : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-[min(22rem,calc(100vw-1.5rem))] p-0"
            sideOffset={8}
          >
            <div className="border-b border-border/60 px-4 py-3">
              <p className="text-sm font-semibold">Filter and sort</p>
            </div>

            <div className="flex flex-col gap-4 p-4">
              <fieldset>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <legend className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Publications
                  </legend>
                  {!allSourcesSelected ? (
                    <button
                      className="text-[11px] font-medium text-primary hover:underline"
                      onClick={() =>
                        setSelectedSources(
                          new Set(sources.map((source) => source.key)),
                        )
                      }
                      type="button"
                    >
                      Select all
                    </button>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">
                      All selected
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {sources.map((source) => {
                    const selected = selectedSources.has(source.key);
                    const lastSelected = selected && selectedSources.size === 1;
                    return (
                      <button
                        aria-pressed={selected}
                        className={cn(
                          "inline-flex min-h-7 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
                          selected
                            ? "border-primary/35 bg-primary/10 text-foreground hover:bg-primary/15"
                            : "border-border/70 bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground",
                          lastSelected && "cursor-not-allowed",
                        )}
                        key={source.key}
                        onClick={() => {
                          if (lastSelected) return;
                          setSelectedSources((current) => {
                            const next = new Set(current);
                            if (selected) next.delete(source.key);
                            else next.add(source.key);
                            return next;
                          });
                        }}
                        title={
                          lastSelected
                            ? "Keep at least one publication selected"
                            : selected
                              ? `Deselect ${source.name}`
                              : `Select ${source.name}`
                        }
                        type="button"
                      >
                        {selected ? (
                          <Check aria-hidden className="h-3 w-3" />
                        ) : null}
                        {source.name}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="min-w-0">
                <div className="min-w-0">
                  <label className="sr-only" htmlFor="notes-coverage-tag">
                    Coverage tag
                  </label>
                  <Popover onOpenChange={setTagOpen} open={tagOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        aria-expanded={tagOpen}
                        className="w-full min-w-0 justify-between px-2.5 text-xs font-normal"
                        id="notes-coverage-tag"
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        <span className="truncate">
                          {selectedTag
                            ? `${selectedTag.label} · ${TAG_KIND_LABELS[selectedTag.kind]}`
                            : "Choose coverage tag"}
                        </span>
                        <ChevronsUpDown
                          aria-hidden
                          className="opacity-60"
                          data-icon="inline-end"
                        />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72 p-0">
                      <Command>
                        <CommandInput placeholder="Ticker, company, theme, model…" />
                        <CommandList>
                          <CommandEmpty>No matching tag found.</CommandEmpty>
                          <CommandGroup heading="Coverage and taxonomy">
                            {tags.map((tag) => (
                              <CommandItem
                                key={tag.key}
                                keywords={[
                                  tag.label,
                                  tag.slug,
                                  TAG_KIND_LABELS[tag.kind],
                                ]}
                                onSelect={() => {
                                  setTagKey(tag.key);
                                  setTagOpen(false);
                                }}
                                value={tag.key}
                              >
                                <Check
                                  aria-hidden
                                  className={cn(
                                    "h-3.5 w-3.5",
                                    tag.key === tagKey
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <span className="min-w-0 flex-1 truncate">
                                  {tag.label}
                                </span>
                                <span className="shrink-0 text-[10px] text-muted-foreground">
                                  {TAG_KIND_LABELS[tag.kind]}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {selectedTag ? (
                <div className="flex items-center gap-2">
                  <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-secondary/10 px-2.5 py-1 text-[11px] font-medium text-foreground">
                    <span className="truncate">{selectedTag.label}</span>
                    <button
                      aria-label={`Remove ${selectedTag.label} tag filter`}
                      className="rounded-full p-0.5 text-muted-foreground hover:bg-background/80 hover:text-foreground"
                      onClick={() => setTagKey("")}
                      type="button"
                    >
                      <X aria-hidden className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-3 atlas-v2:flex-col atlas-v2:items-stretch">
                <span className="text-[11px] text-muted-foreground">
                  {hasSearch
                    ? "Matches every word in titles, summaries, authors and tags. Dates: YYYY-MM-DD or M/D/YYYY (UTC)."
                    : `${selectedSources.size} of ${sources.length} publications selected`}
                </span>
                <div className="flex items-center gap-2 atlas-v2:justify-end">
                  {initialSearch ||
                  initialTagKey ||
                  initialSort !== "newest" ||
                  initialSourceKeys.length < sources.length ? (
                    <Button asChild size="sm" variant="ghost">
                      <a href="/notes">Clear</a>
                    </Button>
                  ) : null}
                  <Button
                    disabled={pending || selectedSources.size === 0}
                    form="notes-feed-filter-form"
                    size="sm"
                    type="submit"
                  >
                    {pending ? "Updating…" : "Update results"}
                  </Button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <label className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        Sort results
        <select
          aria-label="Sort the Notes feed"
          className="h-8 min-w-0 rounded-md border border-border/70 bg-background px-2 text-xs text-foreground outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-65 atlas-v2:border-input atlas-v2:focus:border-ring"
          onChange={(event) => {
            const nextSort = event.target.value as NotesFeedBrowseSort;
            setSort(nextSort);
            apply(nextSort);
          }}
          value={!hasSearch && sort === "relevance" ? "newest" : sort}
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          {hasSearch ? <option value="relevance">Most relevant</option> : null}
        </select>
      </label>
    </form>
  );
}
